import { Component, DestroyRef, effect, inject, OnInit, signal, untracked, ViewChild } from '@angular/core';
import { NgIf } from '@angular/common';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, switchMap, finalize, catchError, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { MessagingService } from '../../../services/messaging.service';
import { DashboardService } from '../../../services/dashboard.service';
import { UtilService } from '../../../services/util.service';
import { StorageService } from '../../../services/storage.service';
import { LocalStorageKeys, MessagingGalleryFilters } from '../../../shared/types/storage';
import { DeviceFilterType } from '../../../services/types';
import { CreativeGalleryComponent } from '../../my-brand-screen/creative-gallery/creative-gallery.component';
import { WidgetContainerComponent } from '../../my-brand-screen/widget-container/widget-container.component';
import { PaginatorComponent } from '../../../shared/components/paginator/paginator.component';
import { SelectItem, SelectMenuComponent, SelectMode } from '../../../shared/components/select-menu/select-menu.component';
import { NoDataWidgetComponent } from '../../../shared/components/no-data-widget/no-data-widget.component';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { AssetVersionPipe } from '../../../pipes/asset-version.pipe';
import {
  CreativeGallerySort,
  GalleryNavigation
} from '../../my-brand-screen/types';
import { WIDGET_QUESTIONS_MAPPING_FOR_DOMAIN } from '../../copilot/types';
import {
  MessagingCreativeGalleryPayload,
  MessagingCreativeGalleryResponse,
  MESSAGING_GALLERY_SORT_OPTIONS,
  GalleryQueryParam
} from '../types';
import { AuthorizationService } from '../../../services/authorization.service';
import { TippyDirective } from '@ngneat/helipopper';
import { PremiumTooltipComponent } from '../../../shared/components/premium-tooltip/premium-tooltip.component';
import { VolumeFilterComponent } from '../../../shared/components/volume-filter/volume-filter.component';
import { tooltips } from '../../../utils/tooltips';
import { PosthogService } from '../../../services/posthog.service';


const DEFAULT_PAGE_SIZE = 12;
const QP = GalleryQueryParam;

@Component({
  selector: 'app-creative-gallery-tab',
  imports: [
    NgIf,
    CreativeGalleryComponent,
    WidgetContainerComponent,
    PaginatorComponent,
    SelectMenuComponent,
    NoDataWidgetComponent,
    LoaderComponent,
    AssetVersionPipe,
    TippyDirective,
    PremiumTooltipComponent,
    VolumeFilterComponent
  ],
  templateUrl: './creative-gallery-tab.component.html',
  styleUrl: './creative-gallery-tab.component.scss'
})
export class CreativeGalleryTabComponent implements OnInit {
  protected readonly SelectMode = SelectMode;
  protected readonly WIDGET_QUESTIONS_MAPPING_FOR_DOMAIN = WIDGET_QUESTIONS_MAPPING_FOR_DOMAIN;
  protected readonly tooltips = tooltips;
  public openSortPanel = signal<Record<string, never> | null>(null);
  public openThemePanel = signal<Record<string, never> | null>(null);

  get isSortOpen(): boolean {
    return this.sortSelectMenu?.isOpen ?? false;
  }

  get isThemeOpen(): boolean {
    return this.themeSelectMenu?.isOpen ?? false;
  }

  public creativeGalleryData: MessagingCreativeGalleryResponse = { creativesData: [], total: 0, themes: [] };
  public gallerySortOptions = MESSAGING_GALLERY_SORT_OPTIONS;
  public themeOptions: SelectItem<string>[] = [];
  public themeControl: FormControl<string[]> = new FormControl<string[]>([], { nonNullable: true });
  public gallerySort: FormControl<CreativeGallerySort> = new FormControl();
  public creativeGalleryCurrentPage = 0;
  public initialPage = 1;
  public loading = false;
  public pageSize = DEFAULT_PAGE_SIZE;
  public pageSizeSelectOptions: SelectItem<number>[] = [
    { label: '12', key: 12 },
    { label: '24', key: 24 },
    { label: '48', key: 48 }
  ];
  public pageSizeControl: FormControl<number | null> = new FormControl(DEFAULT_PAGE_SIZE);

  private creativeGalleryData$ = new Subject<MessagingCreativeGalleryPayload>();
  private pendingGalleryModalNav?: GalleryNavigation;
  private initialLoadComplete = false;
  private queryParamsApplied = false;

  private creativeGalleryVolumeThreshold: number | null = null;

  private destroyRef = inject(DestroyRef);
  private messagingService = inject(MessagingService);
  private dashboardService = inject(DashboardService);
  private utilService = inject(UtilService);
  private storageService = inject(StorageService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private authorizationService = inject(AuthorizationService);
  private posthogService = inject(PosthogService);

  @ViewChild('creativeGalleryPaginator') creativeGalleryPaginator!: PaginatorComponent;
  @ViewChild(CreativeGalleryComponent) creativeGalleryCmp!: CreativeGalleryComponent;
  @ViewChild('sortSelectMenu') sortSelectMenu!: SelectMenuComponent;
  @ViewChild('themeSelectMenu') themeSelectMenu!: SelectMenuComponent;

  constructor() {
    this.loadFiltersFromQueryParams();

    effect(() => {
      const { brandsOptions } = this.dashboardService;
      untracked(brandsOptions);

      if (this.initialLoadComplete) {
        this.creativeGalleryCurrentPage = 0;
        this.creativeGalleryPaginator?.goToPage(1);
      }

      this.loadCreativeGallery();

      if (this.initialLoadComplete) {
        this.updateQueryParams();
      } else {
        this.initialLoadComplete = true;
      }
    });
  }

  ngOnInit(): void {
    this.initCreativeGallerySubject();
    this.loadFiltersFromStorage();
    this.updateQueryParams();
  }

  private loadFiltersFromQueryParams(): void {
    const params = this.route.snapshot.queryParams;

    if (!Object.keys(params).length) return;

    this.queryParamsApplied = true;

    if (params[QP.BRAND_KEY]) {
      this.dashboardService.updateMyBrand(params[QP.BRAND_KEY]);
    }

    if (params[QP.COMPETITOR]) {
      this.dashboardService.setCompetitors(this.parseArrayParam(params[QP.COMPETITOR]));
    }

    if (params[QP.START_DATE] && params[QP.END_DATE]) {
      this.dashboardService.updateDateRange({
        startDate: new Date(params[QP.START_DATE]),
        endDate: new Date(params[QP.END_DATE]),
        relativeDate: params[QP.RELATIVE_DATE] || undefined
      });
    }

    if (params[QP.COUNTRY]) {
      this.dashboardService.updateCountries(this.parseArrayParam(params[QP.COUNTRY]));
    }

    if (params[QP.DEVICE]) {
      this.dashboardService.updateDevice(params[QP.DEVICE] as DeviceFilterType);
    }

    const matchingSort = this.findMatchingSort(params[QP.SORT_FIELD], params[QP.IS_ASC] === 'true');
    this.gallerySort.setValue(matchingSort?.key ?? this.gallerySortOptions[0].key);

    if (params[QP.THEME]) {
      this.themeControl.setValue(this.parseArrayParam(params[QP.THEME]));
    }

    if (params[QP.LIMIT]) {
      const limit = parseInt(params[QP.LIMIT], 10);
      if (this.pageSizeSelectOptions.some(opt => opt.key === limit)) {
        this.pageSize = limit;
        this.pageSizeControl.setValue(limit);
      }
    }

    if (params[QP.PAGE]) {
      const page = parseInt(params[QP.PAGE], 10);
      if (page >= 1) {
        this.creativeGalleryCurrentPage = page - 1;
        this.initialPage = page;
      }
    } else {
      this.creativeGalleryCurrentPage = 0;
      this.initialPage = 1;
    }

    this.storageService.local.setItem(LocalStorageKeys.MESSAGING_GALLERY_FILTERS, {
      sortField: this.gallerySort.value?.sortField,
      isAsc: this.gallerySort.value?.isAsc,
      themes: this.themeControl.value.length ? this.themeControl.value : undefined
    });
  }

  private updateQueryParams(): void {
    const { myBrand, myCompetitors, dateRange, countries, device } = this.dashboardService.brandsFilters();
    const defaultSort = this.gallerySortOptions[0].key;
    const isDefaultSort = !this.gallerySort.value?.sortField ||
      (this.gallerySort.value.sortField === defaultSort.sortField && this.gallerySort.value.isAsc === defaultSort.isAsc);

    const queryParams: Record<string, string | string[] | null> = {
      [QP.BRAND_KEY]: myBrand?.key ?? null,
      [QP.COMPETITOR]: myCompetitors?.length ? myCompetitors.map(c => c.key) : null,
      [QP.START_DATE]: dateRange?.startDate ? this.formatDateParam(dateRange.startDate) : null,
      [QP.END_DATE]: dateRange?.endDate ? this.formatDateParam(dateRange.endDate) : null,
      [QP.RELATIVE_DATE]: dateRange?.relativeDate ?? null,
      [QP.COUNTRY]: countries?.length ? countries : null,
      [QP.DEVICE]: device && device !== DeviceFilterType.ALL_DEVICES ? device : null,
      [QP.SORT_FIELD]: isDefaultSort ? null : this.gallerySort.value.sortField,
      [QP.IS_ASC]: isDefaultSort ? null : String(this.gallerySort.value.isAsc),
      [QP.THEME]: this.themeControl.value.length ? this.themeControl.value : null,
      [QP.PAGE]: this.creativeGalleryCurrentPage > 0 ? String(this.creativeGalleryCurrentPage + 1) : null,
      [QP.LIMIT]: this.pageSize !== DEFAULT_PAGE_SIZE ? String(this.pageSize) : null
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  private loadFiltersFromStorage(): void {
    if (this.queryParamsApplied) return;

    const filters = this.storageService.local.getItem(LocalStorageKeys.MESSAGING_GALLERY_FILTERS);
    const matchingSort = filters ? this.findMatchingSort(filters.sortField, filters.isAsc) : null;

    this.gallerySort.setValue(matchingSort?.key ?? this.gallerySortOptions[0].key);

    if (filters?.themes?.length) {
      this.themeControl.setValue(filters.themes);
    }
  }

  private saveFiltersToStorage(): void {
    const filters: MessagingGalleryFilters = {
      sortField: this.gallerySort.value?.sortField,
      isAsc: this.gallerySort.value?.isAsc,
      themes: this.themeControl.value.length ? this.themeControl.value : undefined
    };
    this.storageService.local.setItem(LocalStorageKeys.MESSAGING_GALLERY_FILTERS, filters);
    this.updateQueryParams();
  }

  private initCreativeGallerySubject(): void {
    this.creativeGalleryData$.pipe(
      switchMap(payload => {
        this.loading = true;
        return this.messagingService.getCreativeGallery(payload).pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
            this.loading = false;
          }),
          catchError(() => of(undefined))
        );
      })
    ).subscribe(result => {
      this.handleGalleryDataResult(result);
      this.handlePendingGalleryNavigation();
    });
  }

  private handleGalleryDataResult(result: MessagingCreativeGalleryResponse | undefined): void {
    if (!result) return;

    this.creativeGalleryData = result;
    this.themeOptions = result.themes.map(theme => ({ label: theme, key: theme }));

    const availableThemes = new Set(result.themes);
    const currentThemes = this.themeControl.value.filter(theme => availableThemes.has(theme));

    if (currentThemes.length !== this.themeControl.value.length) {
      this.themeControl.setValue(currentThemes);
      this.saveFiltersToStorage();
    }
  }

  private handlePendingGalleryNavigation(): void {
    if (!this.pendingGalleryModalNav || !this.creativeGalleryData.creativesData.length) return;

    const openFirst = this.pendingGalleryModalNav === GalleryNavigation.NEXT;
    const target = openFirst
      ? this.creativeGalleryData.creativesData[0]
      : this.creativeGalleryData.creativesData[this.creativeGalleryData.creativesData.length - 1];

    this.pendingGalleryModalNav = undefined;
    setTimeout(() => this.creativeGalleryCmp.openCardModal(target.id, 'navigate'), 0);
  }

  private loadCreativeGallery(): void {
    this.creativeGalleryData$.next({
      brandsKeys: this.getAllBrandsKeys(),
      ...this.gallerySort.value,
      limit: this.pageSize,
      offset: this.creativeGalleryCurrentPage * this.pageSize,
      themes: this.themeControl.value.length ? this.themeControl.value : undefined,
      ...(this.dashboardService.getDashboardFiltersPayload()),
      ...(this.creativeGalleryVolumeThreshold ? { minImpressions: this.creativeGalleryVolumeThreshold } : {})
    });
  }

  private setGalleryDefaultSort(): void {
    this.gallerySort.setValue(this.gallerySortOptions[0].key);
  }

  protected getAllBrandsKeys(): string[] {
    const dashboardFilters = this.dashboardService.brandsFilters();
    const myBrandKey = dashboardFilters.myBrand?.key;
    const competitorKeys = dashboardFilters.myCompetitors.map(c => c.key);
    return myBrandKey ? [myBrandKey, ...competitorKeys] : competitorKeys;
  }

  public onSortFieldChange(): void {
    setTimeout(() => {
      this.openSortPanel.set(null);
      this.sortSelectMenu?.close();
    });
    this.onFilterChange();
  }

  public onThemeChange(): void {
    setTimeout(() => {
      this.openThemePanel.set(null);
      this.themeSelectMenu?.close();
    });
    this.onFilterChange();
  }

  private onFilterChange(): void {
    this.saveFiltersToStorage();
    this.resetToFirstPage();
  }

  private resetToFirstPage(): void {
    this.creativeGalleryPaginator?.goToPage(1);
    this.onCreativeGalleryPageChange({
      pageIndex: 0,
      pageSize: this.pageSize,
      length: this.creativeGalleryData.total,
      previousPageIndex: this.creativeGalleryCurrentPage
    });
  }

  public onCreativeGalleryPageChange(event: PageEvent): void {
    this.creativeGalleryCurrentPage = event.pageIndex as number;
    this.getSortedCreativeGallery();
    this.updateQueryParams();
  }

  public onPageSizeChange(size: number | null): void {
    if (size && size !== this.pageSize) {
      this.pageSize = size;
      this.creativeGalleryCurrentPage = 0;
      this.creativeGalleryPaginator?.goToPage(1);
      this.getSortedCreativeGallery();
      this.updateQueryParams();
    }
  }

  private getSortedCreativeGallery(): void {
    this.creativeGalleryData$.next({
      brandsKeys: this.getAllBrandsKeys(),
      limit: this.pageSize,
      offset: this.creativeGalleryCurrentPage * this.pageSize,
      themes: this.themeControl.value.length ? this.themeControl.value : undefined,
      ...this.gallerySort.value,
      ...(this.dashboardService.getDashboardFiltersPayload()),
      ...(this.creativeGalleryVolumeThreshold ? { minImpressions: this.creativeGalleryVolumeThreshold } : {})
    });
  }

  public onCreativeModalEdgeNav(direction: GalleryNavigation): void {
    const total = this.creativeGalleryData.total;
    const lastPageIndex = Math.max(0, Math.ceil(total / this.pageSize) - 1);
    const targetIndex = direction === GalleryNavigation.NEXT
      ? this.creativeGalleryCurrentPage + 1
      : this.creativeGalleryCurrentPage - 1;

    if (targetIndex < 0) {
      this.pendingGalleryModalNav = direction;
      this.creativeGalleryPaginator?.goToPage(lastPageIndex + 1);
      return;
    }
    if (targetIndex > lastPageIndex) {
      this.pendingGalleryModalNav = direction;
      this.creativeGalleryPaginator?.goToPage(1);
      return;
    }
    this.pendingGalleryModalNav = direction;
    this.creativeGalleryPaginator?.goToPage(targetIndex + 1);
  }

  public onExport(): void {
    if (this.isExportDisabled) return;

    const dashboardFilters = this.dashboardService.brandsFilters();
    const payload = {
      brandsKeys: this.getAllBrandsKeys(),
      themes: this.themeControl.value.length ? this.themeControl.value : undefined,
      ...this.gallerySort.value,
      ...(this.dashboardService.getDashboardFiltersPayload(dashboardFilters))
    };

    this.messagingService.getCreativeGalleryFile(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result?.fileUrl) {
          this.utilService.downloadFileFromUrl(result.fileUrl);
        }
      });
  }

  public onCreativeGalleryVolumeFilter(threshold: number | null): void {
    this.creativeGalleryVolumeThreshold = threshold;
    this.resetToFirstPage();
    this.posthogService.captureVolumeFilter('creative_gallery', threshold);
  }

  private parseArrayParam(param: string | string[]): string[] {
    return Array.isArray(param) ? param : [param];
  }

  private formatDateParam(date: Date | string): string {
    return date instanceof Date ? date.toISOString().split('T')[0] : String(date).split('T')[0];
  }

  private findMatchingSort(sortField?: string, isAsc?: boolean): SelectItem<CreativeGallerySort> | undefined {
    if (!sortField) return undefined;
    return this.gallerySortOptions.find(opt => opt.key.sortField === sortField && opt.key.isAsc === isAsc);
  }

  get isExportDisabled(): boolean {
    return !this.creativeGalleryData.total || this.authorizationService.isTrialUser;
  }

  get isExpiredUser(): boolean {
    return this.authorizationService.isTrialUser;
  }
}
